<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import VueApexCharts from "vue3-apexcharts";
import { useTheme } from "vuetify";
import AppTabs from "@/components/shared/AppTabs.vue";
import CatMascot from "@/components/doctor-performance/cats/CatMascot.vue";
import CatSpeechBubble from "@/components/doctor-performance/cats/CatSpeechBubble.vue";
import AnimatedNumber from "@/components/territory-insights/AnimatedNumber.vue";
import ReturnsAttentionList from "@/components/warehouse/returns-monitor/ReturnsAttentionList.vue";
import ReturnsItemsBoard from "@/components/warehouse/returns-monitor/ReturnsItemsBoard.vue";
import ReturnsKpiTile from "@/components/warehouse/returns-monitor/ReturnsKpiTile.vue";
import ReturnsLeaderboard from "@/components/warehouse/returns-monitor/ReturnsLeaderboard.vue";
import ReturnsOperationsTable from "@/components/warehouse/returns-monitor/ReturnsOperationsTable.vue";
import { useReturnsMonitor } from "@/composables/useReturnsMonitor";
import { localToday } from "@/utils/localDate";
import type { TabItem } from "@/type/tabs";

/**
 * Returns & exchanges monitor, in the doctor-performance style: a talking cat that reacts to
 * what is at risk, counting tiles, and five tabs (overview, may get lost, who, items,
 * operations) over one loaded range.
 *
 * Read-only. Figures are the server's own stored results for each operation.
 */
definePage({
  meta: {
    // Static literal on purpose (definePage is hoisted); mirrors PAGE_KEYS.WAREHOUSE_RETURNS_MONITOR.
    permissionKey: "page.warehouse.returns_monitor",
  },
});

const { t, locale } = useI18n();
const tr = (key: string, params: Record<string, unknown> = {}) => t(`alkokh.returnsMonitor.${key}`, params);
const theme = useTheme();
const m = useReturnsMonitor();
const tab = ref("overview");
const whoTab = ref<"cashier" | "till" | "branch">("cashier");

const fmt = (value: number) => new Intl.NumberFormat(locale.value === "ar" ? "ar-IQ-u-nu-latn" : "en-US", { maximumFractionDigits: 2 }).format(value || 0);
const whole = (value: number) => fmt(Math.round(value));

// --- Period ------------------------------------------------------------------
type Preset = "today" | "last7" | "thisMonth" | "lastMonth" | "custom";
const preset = ref<Preset>("thisMonth");
const presetItems = computed(() => (["today", "last7", "thisMonth", "lastMonth", "custom"] as const).map(value => ({ value, title: tr(`period.${value}`) })));
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
watch(preset, (value) => {
  const today = new Date(`${localToday()}T00:00:00`);
  if (value === "today") { m.range.from = iso(today); m.range.to = iso(today); }
  else if (value === "last7") { const from = new Date(today); from.setDate(from.getDate() - 6); m.range.from = iso(from); m.range.to = iso(today); }
  else if (value === "thisMonth") { m.range.from = iso(new Date(today.getFullYear(), today.getMonth(), 1)); m.range.to = iso(today); }
  else if (value === "lastMonth") { m.range.from = iso(new Date(today.getFullYear(), today.getMonth() - 1, 1)); m.range.to = iso(new Date(today.getFullYear(), today.getMonth(), 0)); }
});
watch(() => [m.range.from, m.range.to], () => { if (m.range.from && m.range.to && m.range.from <= m.range.to) void m.load(); });

// --- Derived -------------------------------------------------------------------
const highCount = computed(() => m.attentionList.value.filter(entry => entry.severity === "high").length);
const severityCounts = computed(() => (["high", "medium", "low"] as const).map(level => ({
  level, count: m.attentionList.value.filter(entry => entry.severity === level).length,
  icon: level === "high" ? "tabler-flame" : level === "medium" ? "tabler-eye" : "tabler-info-circle",
  color: level === "high" ? "error" : level === "medium" ? "warning" : "info",
})));
const flagged = computed(() => new Set(m.attentionList.value.map(entry => entry.operation).filter(Boolean) as string[]));
const firstLoad = computed(() => m.loading.value && !m.loadedAt.value);
const whoRows = computed(() => (whoTab.value === "cashier" ? m.byCashier.value : whoTab.value === "till" ? m.byTill.value : m.byBranch.value));
const whoLabel = (key: string) => (whoTab.value === "cashier" ? m.person(key) : key);
const topCashier = computed(() => m.byCashier.value[0]);

// The cat's line follows the data and changes every few seconds.
const lineIndex = ref(0);
let ticker: ReturnType<typeof setInterval> | null = null;
onMounted(() => { void m.load(); ticker = setInterval(() => { lineIndex.value += 1; }, 6500); });
onBeforeUnmount(() => { if (ticker) clearInterval(ticker); });
const catLines = computed(() => {
  if (m.noAccess.value) return [tr("cat.noAccess")];
  if (firstLoad.value) return [tr("cat.loading")];
  const lines: string[] = [];
  if (highCount.value) lines.push(tr("cat.serious", { n: highCount.value }));
  else if (m.attentionList.value.length) lines.push(tr("cat.check", { n: m.attentionList.value.length }));
  else lines.push(tr("cat.allClear"));
  if (!m.visible.value.length) lines.push(tr("cat.quiet"));
  if (topCashier.value) lines.push(tr("cat.topCashier", { name: m.person(topCashier.value.key), v: fmt(topCashier.value.returnValue) }));
  if (m.kpis.value.cashRefund) lines.push(tr("cat.cashOut", { v: fmt(m.kpis.value.cashRefund) }));
  if (m.openCredit.value) lines.push(tr("cat.openCredit", { v: fmt(m.openCredit.value) }));
  if (m.kpis.value.exchanges) lines.push(tr("cat.exchanges", { n: m.kpis.value.exchanges }));
  return lines;
});
const catLine = computed(() => catLines.value[lineIndex.value % catLines.value.length]);
const catMood = computed(() => (m.noAccess.value || highCount.value ? "grumpy" : m.attentionList.value.length ? "normal" : "happy"));

// --- Tiles and tabs ------------------------------------------------------------
const tiles = computed(() => [
  { key: "returns", icon: "tabler-arrow-back-up", color: "error", label: tr("kpiReturns"), value: m.kpis.value.returnValue, format: whole, caption: tr("kpiReturnsSub", { n: m.kpis.value.returns, u: m.kpis.value.unreceipted }), go: () => { m.filters.type = "return"; tab.value = "operations"; } },
  { key: "exchanges", icon: "tabler-arrows-exchange", color: "success", label: tr("kpiExchanges"), value: m.kpis.value.exchanges, format: whole, caption: tr("kpiExchangesSub", { v: fmt(m.kpis.value.replacementValue) }), go: () => { m.filters.type = "exchange"; tab.value = "operations"; } },
  { key: "cash", icon: "tabler-cash", color: "warning", label: tr("kpiCash"), value: m.kpis.value.cashRefund, format: whole, caption: tr("kpiCashSub"), go: () => { tab.value = "who"; } },
  { key: "credit", icon: "tabler-ticket", color: "info", label: tr("kpiCredit"), value: m.kpis.value.creditIssued, format: whole, caption: tr("kpiCreditSub", { v: fmt(m.openCredit.value), n: m.vouchers.value.length }), go: () => { tab.value = "attention"; } },
  { key: "collected", icon: "tabler-coin", color: "primary", label: tr("kpiCollected"), value: m.kpis.value.collected, format: whole, caption: tr("kpiCollectedSub"), go: () => { m.filters.type = "exchange"; tab.value = "operations"; } },
  { key: "attention", icon: "tabler-alert-octagon", color: highCount.value ? "error" : "success", label: tr("kpiAttention"), value: m.attentionList.value.length, format: whole, caption: tr("kpiAttentionSub", { n: highCount.value }), alert: highCount.value > 0, go: () => { tab.value = "attention"; } },
]);
const tabs = computed<TabItem[]>(() => [
  { value: "overview", label: tr("tabs.overview"), icon: "tabler-layout-dashboard" },
  { value: "attention", label: tr("tabs.attention"), icon: "tabler-alert-octagon", badge: m.attentionList.value.length || null, badgeColor: highCount.value ? "error" : "warning" },
  { value: "who", label: tr("tabs.who"), icon: "tabler-trophy" },
  { value: "items", label: tr("tabs.items"), icon: "tabler-packages", badge: m.items.value.back.length || null, badgeColor: "secondary" },
  { value: "operations", label: tr("tabs.operations"), icon: "tabler-list-details", badge: m.visible.value.length || null, badgeColor: "primary" },
]);
const typeChips = computed(() => [
  { value: "all", label: tr("typeAll"), icon: "tabler-list" },
  { value: "return", label: tr("typeReturn"), icon: "tabler-arrow-back-up" },
  { value: "exchange", label: tr("typeExchange"), icon: "tabler-arrows-exchange" },
  { value: "unreceipted", label: tr("typeUnreceipted"), icon: "tabler-receipt-off" },
  { value: "attention", label: tr("typeAttention"), icon: "tabler-alert-octagon" },
] as const);

// --- Charts --------------------------------------------------------------------
const dark = computed(() => theme.global.current.value.dark);
const axisColor = computed(() => (dark.value ? "#A8A5B5" : "#6D6B77"));
const trendSeries = computed(() => [
  { name: tr("typeReturn"), data: m.chart.value.returns },
  { name: tr("typeExchange"), data: m.chart.value.exchanges },
]);
const trendOptions = computed(() => ({
  chart: { type: "bar", stacked: true, toolbar: { show: false }, fontFamily: "inherit", background: "transparent", animations: { enabled: true, easing: "easeinout", speed: 700, animateGradually: { enabled: true, delay: 40 } } },
  colors: ["#FF4C51", "#28C76F"],
  plotOptions: { bar: { columnWidth: "48%", borderRadius: 6, borderRadiusApplication: "end", borderRadiusWhenStacked: "last" } },
  fill: { type: "gradient", gradient: { shade: "light", type: "vertical", shadeIntensity: 0.4, opacityFrom: 1, opacityTo: 0.75, stops: [0, 100] } },
  dataLabels: { enabled: false },
  grid: { borderColor: dark.value ? "rgba(255,255,255,.07)" : "rgba(47,43,61,.07)", strokeDashArray: 5 },
  legend: { position: "top", horizontalAlign: "start", labels: { colors: axisColor.value }, markers: { radius: 12 } },
  xaxis: { categories: m.chart.value.days.map(day => day.slice(5)), labels: { style: { colors: axisColor.value }, rotate: 0, hideOverlappingLabels: true }, axisBorder: { show: false }, axisTicks: { show: false } },
  yaxis: { labels: { formatter: (v: number) => whole(v), style: { colors: axisColor.value } } },
  tooltip: { theme: dark.value ? "dark" : "light", y: { formatter: (v: number) => fmt(v) } },
}));
const moneySeries = computed(() => [m.kpis.value.cashRefund, m.kpis.value.creditIssued, m.kpis.value.collected, m.kpis.value.debtReduced]);
const moneyLabels = computed(() => [tr("kpiCash"), tr("kpiCredit"), tr("kpiCollected"), tr("debtReduced")]);
const moneyEmpty = computed(() => moneySeries.value.every(v => !v));
const moneyOptions = computed(() => ({
  chart: { type: "donut", fontFamily: "inherit", background: "transparent", animations: { enabled: true, speed: 800 } },
  labels: moneyLabels.value,
  colors: ["#FF9F43", "#00BAD1", "#7367F0", "#28C76F"],
  stroke: { width: 3, colors: [dark.value ? "#2F3349" : "#FFFFFF"] },
  dataLabels: { enabled: false },
  legend: { position: "bottom", labels: { colors: axisColor.value } },
  plotOptions: { pie: { donut: { size: "72%", labels: { show: true, value: { formatter: (v: string) => fmt(Number(v)), color: axisColor.value }, total: { show: true, label: tr("moneyMoved"), color: axisColor.value, formatter: (w: any) => fmt(w.globals.seriesTotals.reduce((a: number, b: number) => a + b, 0)) } } } } },
  tooltip: { theme: dark.value ? "dark" : "light", y: { formatter: (v: number) => fmt(v) } },
}));
</script>

<template>
  <div class="rmon">
    <!-- Header: title, the talking cat, and the filters -->
    <VCard
      elevation="0"
      border
      class="rmon-header mb-4"
    >
      <div
        class="rmon-head"
        :class="{ 'rmon-head--alert': highCount > 0 }"
      >
        <div class="rmon-head__text">
          <h1 class="text-h4 d-flex align-center gap-2 flex-wrap">
            <span class="rmon-head__badge"><VIcon
              icon="tabler-arrow-back-up"
              size="22"
            /></span>
            {{ tr('title') }}
            <VChip
              v-if="m.loadedAt.value"
              size="small"
              color="success"
              variant="tonal"
              prepend-icon="tabler-point-filled"
              class="rmon-live"
            >
              {{ tr('updatedAt', { time: m.loadedAt.value.toTimeString().slice(0, 5) }) }}
            </VChip>
          </h1>
          <p class="text-body-2 text-medium-emphasis mb-0">
            {{ tr('subtitle') }}
          </p>
        </div>
        <div class="rmon-head__mascot">
          <CatSpeechBubble
            :text="catLine"
            tail="end"
          />
          <CatMascot
            :pose="firstLoad ? 'play' : highCount ? 'sit' : 'cheer'"
            :mood="catMood"
            color="orange"
            accessory="glasses"
            :size="96"
          />
        </div>
      </div>

      <div class="rmon-filters">
        <AppSelect
          v-model="preset"
          :items="presetItems"
          density="compact"
          :label="tr('period.label')"
          prepend-inner-icon="tabler-calendar"
          class="rmon-filters__item"
        />
        <template v-if="preset === 'custom'">
          <AppDateTimePicker
            v-model="m.range.from"
            density="compact"
            :label="tr('from')"
            :config="{ enableTime: false, dateFormat: 'Y-m-d' }"
            class="rmon-filters__item"
          />
          <AppDateTimePicker
            v-model="m.range.to"
            density="compact"
            :label="tr('to')"
            :config="{ enableTime: false, dateFormat: 'Y-m-d' }"
            class="rmon-filters__item"
          />
        </template>
        <AppSelect
          v-model="m.filters.branch"
          :items="[{ title: tr('allBranches'), value: '' }, ...m.options.value.branches.map(v => ({ title: v, value: v }))]"
          :label="tr('branch')"
          density="compact"
          prepend-inner-icon="tabler-building-store"
          class="rmon-filters__item"
        />
        <AppSelect
          v-model="m.filters.till"
          :items="[{ title: tr('allTills'), value: '' }, ...m.options.value.tills.map(v => ({ title: v, value: v }))]"
          :label="tr('till')"
          density="compact"
          prepend-inner-icon="tabler-cash-register"
          class="rmon-filters__item"
        />
        <AppSelect
          v-model="m.filters.cashier"
          :items="[{ title: tr('allCashiers'), value: '' }, ...m.options.value.cashiers.map(v => ({ title: m.person(v), value: v }))]"
          :label="tr('cashier')"
          density="compact"
          prepend-inner-icon="tabler-user"
          class="rmon-filters__item"
        />
        <div class="rmon-filters__actions">
          <VProgressCircular
            v-if="m.loading.value"
            indeterminate
            size="20"
            width="2"
            color="primary"
          />
          <VBtn
            icon="tabler-refresh"
            variant="text"
            size="small"
            :aria-label="tr('refresh')"
            @click="m.load()"
          />
        </div>
      </div>
    </VCard>

    <!-- No access / failure: a cat says so, no empty dashboard -->
    <VCard
      v-if="m.noAccess.value || m.error.value"
      elevation="0"
      border
    >
      <VCardText class="rmon-state">
        <CatMascot
          pose="sit"
          mood="grumpy"
          :color="m.noAccess.value ? 'grey' : 'black'"
          :size="120"
        />
        <VAlert
          :type="m.noAccess.value ? 'warning' : 'error'"
          variant="tonal"
          :title="m.noAccess.value ? tr('noAccessTitle') : undefined"
          class="rmon-state__alert"
        >
          {{ m.noAccess.value ? tr('noAccess', { doctype: m.noAccess.value }) : (m.error.value.startsWith('alkokh.') ? t(m.error.value) : m.error.value) }}
          <template
            v-if="m.error.value"
            #append
          >
            <VBtn
              size="small"
              variant="text"
              @click="m.load()"
            >
              {{ tr('retry') }}
            </VBtn>
          </template>
        </VAlert>
      </VCardText>
    </VCard>

    <VCard
      v-else-if="firstLoad"
      elevation="0"
      border
    >
      <VCardText class="rmon-state">
        <CatMascot
          pose="play"
          color="orange"
          :size="120"
        />
        <span class="text-medium-emphasis">{{ tr('cat.loading') }}</span>
      </VCardText>
    </VCard>

    <template v-else>
      <div class="rmon-tiles mb-4">
        <ReturnsKpiTile
          v-for="(tile, index) in tiles"
          :key="tile.key"
          :label="tile.label"
          :value="tile.value"
          :icon="tile.icon"
          :color="tile.color"
          :caption="tile.caption"
          :format="tile.format"
          :index="index"
          :alert="tile.alert"
          @select="tile.go()"
        />
      </div>

      <AppTabs
        v-model="tab"
        :items="tabs"
        variant="underline"
        sync-mode="query"
        sync-key="tab"
        default-tab="overview"
        card
        tabs-class="px-4"
        class="rmon-tabs"
      >
        <!-- Overview -->
        <template #panel-overview>
          <VRow>
            <VCol
              cols="12"
              lg="8"
            >
              <VCard
                elevation="0"
                border
                class="h-100"
              >
                <VCardItem>
                  <template #prepend>
                    <VAvatar
                      color="error"
                      variant="tonal"
                      rounded
                      size="36"
                    >
                      <VIcon
                        icon="tabler-chart-bar"
                        size="20"
                      />
                    </VAvatar>
                  </template>
                  <VCardTitle class="text-body-1 font-weight-bold">
                    {{ tr('chartTitle') }}
                  </VCardTitle>
                  <VCardSubtitle>{{ tr('chartSubtitle') }}</VCardSubtitle>
                </VCardItem>
                <VCardText>
                  <VueApexCharts
                    type="bar"
                    height="300"
                    :options="trendOptions"
                    :series="trendSeries"
                  />
                </VCardText>
              </VCard>
            </VCol>
            <VCol
              cols="12"
              lg="4"
            >
              <VCard
                elevation="0"
                border
                class="h-100"
              >
                <VCardItem>
                  <template #prepend>
                    <VAvatar
                      color="warning"
                      variant="tonal"
                      rounded
                      size="36"
                    >
                      <VIcon
                        icon="tabler-cash-banknote"
                        size="20"
                      />
                    </VAvatar>
                  </template>
                  <VCardTitle class="text-body-1 font-weight-bold">
                    {{ tr('moneyTitle') }}
                  </VCardTitle>
                  <VCardSubtitle>{{ tr('moneySubtitle') }}</VCardSubtitle>
                </VCardItem>
                <VCardText>
                  <div
                    v-if="moneyEmpty"
                    class="rmon-mini-state"
                  >
                    <CatMascot
                      pose="sleep"
                      color="cream"
                      :size="72"
                    />
                    <span class="text-medium-emphasis">{{ tr('noOperations') }}</span>
                  </div>
                  <VueApexCharts
                    v-else
                    type="donut"
                    height="300"
                    :options="moneyOptions"
                    :series="moneySeries"
                  />
                </VCardText>
              </VCard>
            </VCol>
            <VCol
              cols="12"
              lg="6"
            >
              <VCard
                elevation="0"
                border
                class="h-100"
              >
                <VCardItem>
                  <template #prepend>
                    <VAvatar
                      :color="highCount ? 'error' : 'success'"
                      variant="tonal"
                      rounded
                      size="36"
                    >
                      <VIcon
                        icon="tabler-alert-octagon"
                        size="20"
                      />
                    </VAvatar>
                  </template>
                  <VCardTitle class="text-body-1 font-weight-bold">
                    {{ tr('attentionTitle') }}
                  </VCardTitle>
                  <template #append>
                    <VBtn
                      v-if="m.attentionList.value.length > 4"
                      variant="text"
                      size="small"
                      append-icon="tabler-arrow-right"
                      @click="tab = 'attention'"
                    >
                      {{ tr('seeAll', { n: m.attentionList.value.length }) }}
                    </VBtn>
                  </template>
                </VCardItem>
                <VCardText>
                  <ReturnsAttentionList
                    :entries="m.attentionList.value.slice(0, 4)"
                    :format="fmt"
                    :person="m.person"
                  />
                </VCardText>
              </VCard>
            </VCol>
            <VCol
              cols="12"
              lg="6"
            >
              <VCard
                elevation="0"
                border
                class="h-100"
              >
                <VCardItem>
                  <template #prepend>
                    <VAvatar
                      color="warning"
                      variant="tonal"
                      rounded
                      size="36"
                    >
                      <VIcon
                        icon="tabler-trophy"
                        size="20"
                      />
                    </VAvatar>
                  </template>
                  <VCardTitle class="text-body-1 font-weight-bold">
                    {{ tr('leaderTitle') }}
                  </VCardTitle>
                  <VCardSubtitle>{{ tr('leaderSubtitle') }}</VCardSubtitle>
                </VCardItem>
                <VCardText>
                  <ReturnsLeaderboard
                    :rows="m.byCashier.value.slice(0, 3)"
                    :label="m.person"
                    :format="fmt"
                  />
                </VCardText>
              </VCard>
            </VCol>
          </VRow>
        </template>

        <!-- What may get lost -->
        <template #panel-attention>
          <div class="rmon-severity mb-4">
            <div
              v-for="s in severityCounts"
              :key="s.level"
              class="rmon-severity__pill"
              :style="{ '--sev': `var(--v-theme-${s.color})` }"
            >
              <VIcon
                :icon="s.icon"
                size="22"
              />
              <strong><AnimatedNumber :value="s.count" /></strong>
              <span>{{ tr(`attention.severity.${s.level}`) }}</span>
            </div>
          </div>
          <VCard
            elevation="0"
            border
          >
            <VCardItem>
              <VCardTitle class="text-body-1 font-weight-bold">
                {{ tr('attentionTitle') }}
              </VCardTitle>
              <VCardSubtitle>{{ tr('attentionSubtitle') }}</VCardSubtitle>
            </VCardItem>
            <VCardText>
              <ReturnsAttentionList
                :entries="m.attentionList.value"
                :format="fmt"
                :person="m.person"
              />
            </VCardText>
          </VCard>
        </template>

        <!-- Who and where -->
        <template #panel-who>
          <VCard
            elevation="0"
            border
          >
            <VCardItem>
              <template #prepend>
                <VAvatar
                  color="warning"
                  variant="tonal"
                  rounded
                  size="36"
                >
                  <VIcon
                    icon="tabler-trophy"
                    size="20"
                  />
                </VAvatar>
              </template>
              <VCardTitle class="text-body-1 font-weight-bold">
                {{ tr('breakdownTitle') }}
              </VCardTitle>
              <template #append>
                <VBtnToggle
                  v-model="whoTab"
                  mandatory
                  density="compact"
                  variant="outlined"
                  divided
                  color="primary"
                >
                  <VBtn
                    value="cashier"
                    prepend-icon="tabler-user"
                  >
                    {{ tr('cashier') }}
                  </VBtn>
                  <VBtn
                    value="till"
                    prepend-icon="tabler-cash-register"
                  >
                    {{ tr('till') }}
                  </VBtn>
                  <VBtn
                    value="branch"
                    prepend-icon="tabler-building-store"
                  >
                    {{ tr('branch') }}
                  </VBtn>
                </VBtnToggle>
              </template>
            </VCardItem>
            <VCardText>
              <ReturnsLeaderboard
                :key="whoTab"
                :rows="whoRows"
                :label="whoLabel"
                :format="fmt"
              />
            </VCardText>
          </VCard>
        </template>

        <!-- Items -->
        <template #panel-items>
          <ReturnsItemsBoard
            :back="m.items.value.back"
            :out="m.items.value.out"
            :format="fmt"
          />
        </template>

        <!-- Operations -->
        <template #panel-operations>
          <VCard
            elevation="0"
            border
          >
            <VCardItem>
              <VCardTitle class="text-body-1 font-weight-bold">
                {{ tr('operationsTitle') }} ({{ m.visible.value.length }})
              </VCardTitle>
              <VCardSubtitle>{{ tr('operationsSubtitle') }}</VCardSubtitle>
            </VCardItem>
            <VCardText>
              <VChipGroup
                v-model="m.filters.type"
                mandatory
                selected-class="text-primary"
                class="rmon-types mb-3"
              >
                <VChip
                  v-for="chip in typeChips"
                  :key="chip.value"
                  :value="chip.value"
                  :prepend-icon="chip.icon"
                  variant="outlined"
                  filter
                >
                  {{ chip.label }}
                </VChip>
              </VChipGroup>
              <ReturnsOperationsTable
                :operations="m.visible.value"
                :format="fmt"
                :person="m.person"
                :flagged="flagged"
              />
            </VCardText>
          </VCard>
        </template>
      </AppTabs>
    </template>
  </div>
</template>

<style scoped>
.rmon :deep(.app-tabs__window) { margin-block-start: 16px; overflow: visible; }
.rmon-head {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  padding-block: 20px 4px;
  padding-inline: 20px;
  background:
    radial-gradient(circle at 92% 30%, rgba(var(--v-theme-error), .16), transparent 40%),
    radial-gradient(circle at 0% 0%, rgba(var(--v-theme-primary), .12), transparent 45%),
    radial-gradient(circle at 55% 120%, rgba(var(--v-theme-success), .1), transparent 50%);
  transition: background .6s;
}
.rmon-head--alert { background: radial-gradient(circle at 92% 30%, rgba(var(--v-theme-error), .26), transparent 42%), radial-gradient(circle at 0% 0%, rgba(var(--v-theme-warning), .14), transparent 45%); }
.rmon-head__text { flex: 1 1 280px; padding-block-end: 12px; }
.rmon-head__badge { display: grid; place-items: center; inline-size: 38px; block-size: 38px; border-radius: 12px; background: rgba(var(--v-theme-error), .14); color: rgb(var(--v-theme-error)); }
.rmon-head__mascot { display: flex; align-items: flex-end; gap: 6px; margin-block-end: -10px; }
.rmon-live :deep(.v-icon) { animation: rmon-blink 1.6s ease-in-out infinite; }
.rmon-filters { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; padding-block: 14px; padding-inline: 20px; border-block-start: 1px solid rgba(var(--v-theme-on-surface), .08); }
.rmon-filters__item { flex: 1 1 170px; max-inline-size: 240px; }
.rmon-filters__actions { display: flex; align-items: center; gap: 2px; margin-inline-start: auto; }
.rmon-tiles { display: grid; gap: 16px; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); }
.rmon-state { display: flex; flex-direction: column; align-items: center; gap: 12px; padding-block: 48px; text-align: center; }
.rmon-state__alert { max-inline-size: 640px; text-align: start; }
.rmon-mini-state { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px; min-block-size: 260px; }
.rmon-severity { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); }
.rmon-severity__pill {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border: 1px solid rgba(var(--sev), .3);
  border-radius: 14px;
  background: linear-gradient(135deg, rgba(var(--sev), .14), rgba(var(--sev), .03));
  color: rgb(var(--sev));
  animation: rmon-pop .45s cubic-bezier(.22, 1, .36, 1) both;
}
.rmon-severity__pill strong { font-size: 1.6rem; font-weight: 900; }
.rmon-severity__pill span { font-weight: 700; }
@keyframes rmon-blink { 50% { opacity: .3; } }
@keyframes rmon-pop { from { opacity: 0; transform: scale(.94); } }
@media (max-width: 600px) {
  .rmon-head__mascot { display: none; }
  .rmon-filters__item { max-inline-size: none; }
}
@media (prefers-reduced-motion: reduce) { .rmon-live :deep(.v-icon), .rmon-severity__pill { animation: none !important; } }
</style>
